import api, { setToken } from './api'
import router from '@/router'
import i18n from '@/i18n'

export const JWT_KEY = 'geox_auth_data'

export const USER_ROLES = {
  DIRECTOR: 'DIRECTOR',
  CENTRAL: 'CENTRAL',
  CENTRAL_ADMIN: 'CENTRAL_ADMIN',
  AGENCY: 'AGENCY',
  AGENCY_EXTERIOR: 'AGENCY_EXTERIOR',
  AGENCY_PATRIMONY: 'AGENCY_PATRIMONY',
  ROOT: 'ROOT'
}

export const USER_ROLES_LABELS = {
  DIRECTOR: i18n.t('DIRECTOR'),
  CENTRAL: i18n.t('CENTRAL'),
  CENTRAL_ADMIN: i18n.t('CENTRAL_ADMIN'),
  AGENCY: i18n.t('AGENCY'),
  AGENCY_EXTERIOR: i18n.t('AGENCY_EXTERIOR'),
  AGENCY_PATRIMONY: i18n.t('AGENCY_PATRIMONY'),
  ROOT: i18n.t('ROOT')
}

export default {
  refreshGapMs: 5000,
  refreshTimeoutId: null,
  authData: null,
  loading: false,
  get userData () {
    return this.authData && this.authData.user
  },
  async init (authData, remember = true) {
    if (!authData) {
      return
    }

    this.loading = true
    if (this.isExpired(authData.refreshExpireTime)) {
      return
    }

    if (this.isExpired(authData.accessExpireTime)) {
      try {
        authData = await this.refreshTokens(authData.refreshToken)
      } catch (err) {
        // eslint-disable-next-line
        console.error(err)
      }
    }

    this.authData = authData
    setToken(authData.accessToken)
    if (remember) {
      localStorage.setItem(JWT_KEY, JSON.stringify(authData))
    }

    this.clearRefreshTimer()
    this.setupRefreshTimer()

    try {
      const userData = await this.fetchUserData()
      this.authData.user = userData
    } catch (err) {
      // eslint-disable-next-line
      console.error(err)
    }
    this.loading = false
  },
  decodeToken (token) {
    return JSON.parse(window.atob(token.split('.')[1]))
  },
  buildAuthData ({ access, refresh }) {
    const accessData = this.decodeToken(access)
    const refreshData = this.decodeToken(refresh)
    return {
      accessToken: access,
      refreshToken: refresh,
      accessExpireTime: accessData.exp * 1000,
      refreshExpireTime: refreshData.exp * 1000,
      user: {
        id: accessData.user_id,
        first_name: accessData.first_name,
        last_name: accessData.last_name,
        role: null
      }
    }
  },
  getAuthData () {
    return this.authData || JSON.parse(localStorage.getItem(JWT_KEY) || 'null')
  },
  restoreAuth () {
    const authData = this.getAuthData()
    return this.init(authData)
  },
  isExpired (time) {
    return Date.now() >= time
  },
  isLoggedIn () {
    let authData = this.authData
    if (!authData) {
      const savedData = localStorage.getItem(JWT_KEY)
      if (savedData) {
        authData = JSON.parse(savedData)
      }
    }
    return authData && !this.isExpired(authData.accessExpireTime)
  },
  async fetchUserData () {
    if (!this.userData || !this.userData.id) {
      return Promise.reject(new Error('[authService.js] cannot fetch user data if authData.user.id is not loaded first'))
    }
    const res = await api.get(`/users/${this.userData.id}`)
    return res.data
  },
  async login ({ username, password }) {
    const res = await api.post('/auth/token', { username, password })
    const newAuthData = this.buildAuthData(res.data)
    await this.init(newAuthData)
  },
  async refreshTokens (refreshToken) {
    const res = await api.post('/auth/token/refresh', { refresh: refreshToken }, {
      headers: {
        'allowRequestDuplication': 'true'
      }
    })
    const newAuthData = this.buildAuthData(res.data)
    return newAuthData
  },
  async refreshAuth () {
    const authData = this.getAuthData()
    if (authData && authData.refreshToken) {
      const newAuthData = await this.refreshTokens(authData.refreshToken)
      await this.init(newAuthData)
    } else {
      // eslint-disable-next-line
      console.error(new Error('[authService.js] Trying to call refreshAuth without a refresh token'))
    }
  },
  logout (doRedirect = true) {
    this.authData = null
    localStorage.removeItem(JWT_KEY)
    this.clearRefreshTimer()
    router.push({
      name: 'login',
      query: doRedirect
        ? { next: router.currentRoute.fullPath }
        : undefined
    })
  },
  clearRefreshTimer () {
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId)
      this.refreshTimeoutId = null
    }
  },
  setupRefreshTimer () {
    const nextRefreshTime = this.authData.accessExpireTime - this.refreshGapMs
    const timeToRefresh = nextRefreshTime - Date.now()

    this.refreshTimeoutId = setTimeout(() => {
      this.refreshAuth()
    }, timeToRefresh)
  }
}
